import React from 'react';
import { Hammer } from 'lucide-react';
import { T } from '../../lib/designTokens';
import {
  AUFTRAGNEHMER_FELDER, AUFTRAGNEHMER_VORGABEN, EIGENER_AUFTRAGNEHMER, auftragnehmerVollstaendig, eegTerminFehler, werkvertragVollstaendig,
} from '../../lib/werkvertrag';

const FELD_STIL = { width: '100%', minHeight: 30, padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 11, color: '#0F172A', background: '#FFFFFF' };
const LABEL_STIL = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10.5, color: T.muted };

/**
 * Angaben zum Werkliefervertrag, die in keinem Bitrix-Feld stehen
 * (src/lib/werkvertrag.js): der Auftragnehmer („Verkäufer“) – fest gewählt
 * oder von Hand – samt Gerichtsort und das Datum des Angebots. Der
 * Auftraggeber kommt aus dem Block darunter. Dazu der Wahlbaustein
 * „EEG-Inbetriebnahme absichern“ mit seinen zwei Terminen.
 *
 * @param {{
 *   angaben: { angebotsdatum: string, verkaeufer: string, auftragnehmer: Object<string, string>,
 *     eegAbsicherung: boolean, montagebeginn: string, eegInbetriebnahme: string },
 *   onAendern: (feld: 'angebotsdatum' | 'montagebeginn' | 'eegInbetriebnahme', wert: string) => void,
 *   onVerkaeufer: (id: string) => void,
 *   onAuftragnehmer: (feld: string, wert: string) => void,
 *   onEeg: (an: boolean) => void,
 *   eegBausteinFehlt?: boolean,
 * }} props
 */
export default function WerkvertragAngaben({ angaben, onAendern, onVerkaeufer, onAuftragnehmer, onEeg, eegBausteinFehlt = false }) {
  const vollstaendig = werkvertragVollstaendig(angaben);
  const verkaeufer = angaben?.verkaeufer || '';
  const auftragnehmer = angaben?.auftragnehmer || {};
  const vonHand = verkaeufer === EIGENER_AUFTRAGNEHMER;
  const eegAn = Boolean(angaben?.eegAbsicherung);
  const terminFehler = eegTerminFehler(angaben);
  return (
    <div style={{ marginTop: 10, border: '1px solid #D7E2F1', borderRadius: 8, background: '#FFFFFF', overflow: 'hidden' }}>
      <div style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 6, color: '#122A5C', fontSize: 11, fontWeight: 700 }}>
        <Hammer size={13} />
        Werkliefervertrag
        <span style={{ marginLeft: 'auto', color: vollstaendig ? '#0F766E' : '#B45309', fontSize: 10, fontWeight: 700 }}>
          {vollstaendig ? 'vollständig' : 'offen'}
        </span>
      </div>
      <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={LABEL_STIL}>
          Verkäufer / Auftragnehmer
          <select value={verkaeufer} onChange={(event) => onVerkaeufer(event.target.value)} style={{ ...FELD_STIL, minHeight: 32 }}>
            <option value="">Bitte wählen …</option>
            {AUFTRAGNEHMER_VORGABEN.map((v) => (
              <option key={v.id} value={v.id}>{v.label}</option>
            ))}
            <option value={EIGENER_AUFTRAGNEHMER}>Anderer Auftragnehmer (von Hand) …</option>
          </select>
        </label>
        {vonHand ? (
          AUFTRAGNEHMER_FELDER.map(([feld, beschriftung]) => (
            <input
              key={feld}
              value={auftragnehmer[feld] || ''}
              onChange={(event) => onAuftragnehmer(feld, event.target.value)}
              placeholder={beschriftung}
              aria-label={beschriftung}
              style={FELD_STIL}
            />
          ))
        ) : verkaeufer ? (
          <div style={{ padding: '6px 8px', border: '1px solid #E2E8F0', borderRadius: 7, background: '#F8FAFC', color: '#334155', fontSize: 10.5, lineHeight: 1.45 }}>
            <strong style={{ color: '#0F172A' }}>{auftragnehmer.firma}</strong><br />
            {auftragnehmer.strasse}, {auftragnehmer.plzOrt}<br />
            vertreten durch {auftragnehmer.vertreter}{auftragnehmer.funktion ? `, ${auftragnehmer.funktion}` : ''}<br />
            Gerichtsort: {auftragnehmer.gerichtsort}
          </div>
        ) : null}
        {vonHand && !auftragnehmerVollstaendig(angaben) ? (
          <div style={{ color: '#B45309', fontSize: 10.5, lineHeight: 1.35 }}>Firma, Anschrift, Vertreter und Gerichtsort ausfüllen – die Funktion darf fehlen.</div>
        ) : null}
        <label style={LABEL_STIL}>
          Datum des Angebots (Anlage 1)
          <input type="date" value={angaben?.angebotsdatum || ''} onChange={(event) => onAendern('angebotsdatum', event.target.value)} style={FELD_STIL} />
        </label>
        <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, color: '#0F172A', fontWeight: 600, cursor: 'pointer' }}>
            <input type="checkbox" checked={eegAn} onChange={(event) => onEeg(event.target.checked)} style={{ marginTop: 2 }} />
            <span>
              EEG-Inbetriebnahme absichern
              <span style={{ display: 'block', color: T.muted, fontSize: 10.5, fontWeight: 400, lineHeight: 1.35 }}>
                Vorbemerkung (6) und § 5.4–5.9: feste Termine, Vertragsstrafe 0,2 % je Werktag – alle Ansprüche wegen Verzugs zusammen höchstens 5 % –, Rücktritt erst nach Nachfrist. Inbetriebnahme heißt: Wechselrichter haben erstmals Strom erzeugt, auch ohne Netzanschluss.
              </span>
            </span>
          </label>
          {eegAn ? (
            <>
              <label style={LABEL_STIL}>
                Montagebeginn spätestens
                <input type="date" value={angaben?.montagebeginn || ''} onChange={(event) => onAendern('montagebeginn', event.target.value)} style={FELD_STIL} />
              </label>
              <label style={LABEL_STIL}>
                EEG-Inbetriebnahme spätestens
                <input type="date" value={angaben?.eegInbetriebnahme || ''} onChange={(event) => onAendern('eegInbetriebnahme', event.target.value)} style={FELD_STIL} />
              </label>
              {terminFehler ? <div style={{ color: '#B45309', fontSize: 10.5, lineHeight: 1.35 }}>{terminFehler}</div> : null}
              {eegBausteinFehlt ? (
                <div style={{ color: '#B91C1C', fontSize: 10.5, lineHeight: 1.35 }}>
                  Die gespeicherte Vorlage enthält diese Absätze nicht. Unter Vorlagen › Verträge auf das Original zurücksetzen oder die Absätze dort übernehmen.
                </div>
              ) : null}
            </>
          ) : null}
          <div style={{ color: T.muted, fontSize: 10.5, lineHeight: 1.35 }}>Wirkt beim Füllen – nach einer Änderung neu füllen.</div>
        </div>
        <div style={{ color: T.muted, fontSize: 10.5, lineHeight: 1.35 }}>
          Anschrift der Fläche und kWp kommen aus Bitrix. Der Vertrag wird nicht in Bitrix hochgeladen – nach dem Füllen als PDF oder HTML herunterladen.
        </div>
      </div>
    </div>
  );
}
